import { useEffect, useState } from 'react';
import {
  ACTUAL_METHOD_LABELS,
  RECONCILIATION_STATUSES,
  RECONCILIATION_STATUS_LABELS,
  centsToPesos,
  type PaymentMethod,
  type ReconciliationStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.7 — Daily Cash Reconciliation & Handover Accountability.
 *
 * An administrator-controlled, branch-per-day process:
 *
 *   RECORDED  - collections derived server-side from the payment records
 *               (the receiving payment, dated by its Manila business day).
 *   ACTUAL    - what the preparer physically counted (CASH) or verified
 *               (GCASH/MAYA) / entered as-is (OTHER), in integer centavos.
 *   DIFFERENCE- recorded - actual, signed, shown per method and in total.
 *
 * Honesty rules baked into this view: a payment record does NOT prove
 * cash was received (the actual side is a person's attestation), GCash/
 * Maya are preparer-verified only (no external account check exists),
 * OTHER is never silently classified as cash, a missing count shows
 * "Not counted" instead of ₱0, money recorded without a method is shown
 * as unmatched, and differences are never auto-resolved. Submitted and
 * reviewed records are frozen server-side; every mutation is attributed
 * and audited (history shown in the detail). The client never computes
 * authoritative totals - every figure below comes from the server.
 */

interface ReconMethodLine {
  method: string;
  label: string;
  recordedCents: number;
  actualCents: number | null;
  differenceCents: number | null;
  counted: boolean;
}

interface ReconDto {
  id: string;
  branch: { id: string; code: string };
  businessDate: string;
  status: ReconciliationStatus;
  statusLabel: string;
  recorded: {
    totalCents: number;
    payments: number;
    byMethod: { method: string; payments: number; collectedCents: number }[];
  };
  /** Frozen as-of moment once submitted; null while draft (live view). */
  recordedAsOf: string | null;
  /** Live minus snapshot: recorded for this day after submission (disclosed, never folded in). */
  recordedAfterSubmissionCents: number;
  methods: ReconMethodLine[];
  actualTotalCents: number | null;
  /** Signed: positive = recorded exceeds counted (shortage), negative = excess. */
  differenceCents: number | null;
  unmatchedRecordedCents: number;
  missingMethods: string[];
  incomplete: boolean;
  notes: string | null;
  evidenceNote: string | null;
  reviewNotes: string | null;
  preparedBy: { id: string | null; name: string } | null;
  submittedBy: { id: string | null; name: string } | null;
  submittedAt: string | null;
  reviewedBy: { id: string | null; name: string } | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ReconListResponse {
  total: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  status: ReconciliationStatus | null;
  items: ReconDto[];
}

interface HistoryEntry {
  action: string;
  label: string;
  actorName: string;
  actorRole: string;
  metadata: unknown;
  createdAt: string;
}

interface ReconDetailResponse {
  timeZone: string;
  reconciliation: ReconDto;
  history: HistoryEntry[];
}

const PAGE_SIZE = 20;

const STATUS_TONE: Record<ReconciliationStatus, 'slate' | 'indigo' | 'emerald'> = {
  DRAFT: 'slate',
  SUBMITTED: 'indigo',
  REVIEWED: 'emerald',
};

function StatusBadge({ status }: { status: ReconciliationStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{RECONCILIATION_STATUS_LABELS[status]}</Badge>;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Manila calendar date for shortcuts (the server re-parses dates as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

/**
 * Signed difference display: shortage (recorded > counted) in rose,
 * excess (counted > recorded) in amber, match neutral, and a missing
 * count shown as "Not counted" — never as ₱0.
 */
function DifferenceCell({ cents }: { cents: number | null }) {
  if (cents === null) {
    return (
      <span className="italic text-slate-400" title="No actual figure counted yet for every method">
        Not counted
      </span>
    );
  }
  if (cents === 0) {
    return <span className="tabular-nums text-slate-500">{centsToPesos(0)}</span>;
  }
  if (cents > 0) {
    return (
      <span
        className="font-semibold tabular-nums text-rose-700"
        title="Recorded collections exceed the counted/verified amount (shortage)"
      >
        <Badge tone="rose">Short {centsToPesos(cents)}</Badge>
      </span>
    );
  }
  return (
    <span
      className="font-semibold tabular-nums text-amber-700"
      title="Counted/verified amount exceeds what was recorded (excess)"
    >
      <Badge tone="amber">Excess {centsToPesos(-cents)}</Badge>
    </span>
  );
}

const NOT_COUNTED = <span className="italic text-slate-400">Not counted</span>;

/** Peso string input -> integer cents (null when blank). Server re-validates. */
function inputToCents(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return Math.round(parsed * 100);
}

function centsToInput(cents: number | null): string {
  return cents === null ? '' : String(cents / 100);
}

interface FormState {
  mode: 'create' | 'edit';
  id?: string;
  branch: string;
  businessDate: string;
  cash: string;
  gcash: string;
  maya: string;
  other: string;
  notes: string;
  evidenceNote: string;
}

export function ReconciliationPage() {
  const { branchFilter, setBranchFilter, branches } = useAuth();

  const [list, setList] = useState<ReconListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-validated server-side on each request.
  const [statusFilter, setStatusFilter] = useState<'' | ReconciliationStatus>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [qFilter, setQFilter] = useState('');
  const [page, setPage] = useState(0);

  // Detail modal (+ its history and actions).
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReconDetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Submit confirmation (freezes the as-of snapshot).
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  // Review dialog (attribution + optional note).
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  // Create / edit-draft form.
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  const scopeParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    return params;
  };

  // List (paginated, deterministic: business date desc, branch asc).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = scopeParams();
    if (statusFilter) params.set('status', statusFilter);
    if (qFilter) params.set('q', qFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    void api
      .get<ReconListResponse>(`/reconciliations?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setList(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : 'Failed to load reconciliations.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromFilter, toFilter, statusFilter, qFilter, page, refreshKey]);

  // Detail (fetched only while its modal is open).
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      setActionError(null);
      setConfirmSubmit(false);
      setReviewOpen(false);
      setReviewNotes('');
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    void api
      .get<ReconDetailResponse>(`/reconciliations/${detailId}?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(
            err instanceof ApiError ? err.message : 'Failed to load the reconciliation.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const resetPageAnd = (fn: () => void) => {
    setPage(0);
    fn();
  };

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = list?.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = Boolean(statusFilter || qFilter || fromFilter || toFilter);

  const openCreate = () => {
    setFormError(null);
    setForm({
      mode: 'create',
      branch: branchFilter === 'ALL' ? (branches[0]?.code ?? '') : branchFilter,
      businessDate: manilaToday(),
      cash: '',
      gcash: '',
      maya: '',
      other: '',
      notes: '',
      evidenceNote: '',
    });
  };

  const openEdit = (row: ReconDto) => {
    const byMethod = (m: string) =>
      row.methods.find((line) => line.method === m)?.actualCents ?? null;
    setFormError(null);
    setForm({
      mode: 'edit',
      id: row.id,
      branch: row.branch.code,
      businessDate: row.businessDate,
      cash: centsToInput(byMethod('CASH')),
      gcash: centsToInput(byMethod('GCASH')),
      maya: centsToInput(byMethod('MAYA')),
      other: centsToInput(byMethod('OTHER')),
      notes: row.notes ?? '',
      evidenceNote: row.evidenceNote ?? '',
    });
  };

  /** Shared async action wrapper: busy flag, error surface, list+detail refresh on success. */
  const runAction = async (action: () => Promise<void>) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await action();
      refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitDraft = () =>
    runAction(async () => {
      await api.post(`/reconciliations/${detailId}/submit`);
      setConfirmSubmit(false);
    });

  const reviewDraft = () =>
    runAction(async () => {
      await api.post(`/reconciliations/${detailId}/review`, {
        reviewNotes: reviewNotes.trim() || undefined,
      });
      setReviewOpen(false);
      setReviewNotes('');
    });

  /** Create a draft or save draft corrections (server validates and re-computes everything). */
  const saveForm = () => {
    if (!form) return;
    const values = {
      cashCountedCents: inputToCents(form.cash),
      gcashVerifiedCents: inputToCents(form.gcash),
      mayaVerifiedCents: inputToCents(form.maya),
      otherActualCents: inputToCents(form.other),
    };
    if (Object.values(values).some((v) => v === undefined)) {
      setFormError('Amounts must be zero or positive numbers (or left blank).');
      return;
    }
    setFormBusy(true);
    setFormError(null);
    const payload = {
      ...values,
      notes: form.mode === 'edit' ? form.notes.trim() || null : form.notes.trim() || undefined,
      evidenceNote:
        form.mode === 'edit'
          ? form.evidenceNote.trim() || null
          : form.evidenceNote.trim() || undefined,
    };
    const request =
      form.mode === 'create'
        ? api.post<{ reconciliation: ReconDto }>('/reconciliations', {
            branch: form.branch,
            businessDate: form.businessDate,
            ...payload,
          })
        : api.patch<{ reconciliation: ReconDto }>(`/reconciliations/${form.id}`, payload);
    void request
      .then((res) => {
        setForm(null);
        setDetailId(res.reconciliation.id);
        refresh();
      })
      .catch((err: unknown) => {
        setFormError(err instanceof ApiError ? err.message : 'Failed to save the reconciliation.');
      })
      .finally(() => setFormBusy(false));
  };

  return (
    <section className="space-y-4" aria-label="Cash reconciliation">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Cash Reconciliation</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · recorded collections vs counted/verified amounts, per branch and
            Asia/Manila business day — prepared, submitted and reviewed with attribution.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Icon name="plus" className="h-5 w-5" />
            New reconciliation
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-700">Recorded</span> collections come from the
          payment records the system already keeps (one payment at receiving, dated by its Manila
          business day) — a payment record{' '}
          <span className="font-semibold text-slate-700">does not prove cash was received</span>.
          The <span className="font-semibold text-slate-700">counted/verified</span> side is the
          preparer&apos;s attestation: cash counted physically, GCash/Maya verified by the preparer
          (no external account verification exists), Other entered as-is and never classified as
          cash. A missing count shows <span className="italic">Not counted</span>, never ₱0;
          differences stay visible and are never auto-resolved.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {/* Filters — branch mirrors the header switcher; the rest re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Branch
          <select
            value={branchFilter}
            onChange={(e) => resetPageAnd(() => setBranchFilter(e.target.value))}
            className={inputCls}
          >
            <option value="ALL">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.code}>
                {branch.code} — {branch.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Status
          <select
            value={statusFilter}
            onChange={(e) =>
              resetPageAnd(() => setStatusFilter(e.target.value as '' | ReconciliationStatus))
            }
            className={inputCls}
          >
            <option value="">All statuses</option>
            {RECONCILIATION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {RECONCILIATION_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From (business date)
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => resetPageAnd(() => setFromFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To (business date)
          <input
            type="date"
            value={toFilter}
            onChange={(e) => resetPageAnd(() => setToFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() =>
            resetPageAnd(() => {
              setFromFilter(manilaToday());
              setToFilter(manilaToday());
            })
          }
        >
          Today
        </Button>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            resetPageAnd(() => setQFilter(searchInput.trim()));
          }}
        >
          <label className="flex min-w-56 flex-col gap-1 text-xs font-semibold text-slate-500">
            Branch code or person
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="LH1, Maria…"
              className={inputCls}
            />
          </label>
          <Button variant="secondary" type="submit">
            <Icon name="search" className="h-5 w-5" />
            Search
          </Button>
        </form>
        {hasFilters && (
          <Button
            variant="secondary"
            onClick={() =>
              resetPageAnd(() => {
                setStatusFilter('');
                setFromFilter('');
                setToFilter('');
                setSearchInput('');
                setQFilter('');
              })
            }
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila){list?.range
            ? ` · ${list.range.from ?? '…'} → ${list.range.to ?? '…'}`
            : ''}
        </span>
      </Card>

      {/* Reconciliation list. */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Daily reconciliations</h2>
          {list && total > 0 && (
            <p className="text-sm text-slate-500">
              Showing {showingFrom}–{showingTo} of {total} reconciliation
              {total === 1 ? '' : 's'} · newest business date first
            </p>
          )}
        </div>

        {loading && !list ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading reconciliations…
          </div>
        ) : !list || list.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="check" className="h-8 w-8 text-emerald-400" />
            <p className="mt-2 font-medium text-slate-700">No reconciliations match these filters.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasFilters
                ? 'Clear a filter to widen the search.'
                : 'Create one with “New reconciliation” to start counting a branch day.'}
            </p>
          </Card>
        ) : (
          <>
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">Business date</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Recorded</th>
                    <th className="px-3 py-3 text-right">Counted / verified</th>
                    <th className="px-4 py-3 text-right">Difference</th>
                    <th className="px-4 py-3">Prepared by</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Reviewed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.items.map((row) => (
                    <tr key={row.id} className="align-top hover:bg-slate-100/70">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDetailId(row.id)}
                          className="font-semibold text-indigo-600 underline-offset-2 hover:underline"
                          title="Open reconciliation detail"
                        >
                          {row.businessDate}
                        </button>
                        {row.incomplete && (
                          <p className="mt-0.5 text-xs text-amber-700">
                            {4 - row.missingMethods.length} of 4 counted
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{row.branch.code}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {centsToPesos(row.recorded.totalCents)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {row.actualTotalCents === null ? (
                          <span className="text-amber-700">
                            {4 - row.missingMethods.length} of 4
                          </span>
                        ) : (
                          centsToPesos(row.actualTotalCents)
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DifferenceCell cents={row.differenceCents} />
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.preparedBy ? row.preparedBy.name : (
                          <span className="italic text-slate-400">Not recorded</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.submittedAt ? (
                          <>
                            <span className="block">{formatWhen(row.submittedAt)}</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {row.submittedBy?.name}
                            </span>
                          </>
                        ) : (
                          <span className="italic text-slate-400">Not submitted</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.reviewedAt ? (
                          <>
                            <span className="block">{formatWhen(row.reviewedAt)}</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {row.reviewedBy?.name}
                            </span>
                          </>
                        ) : (
                          <span className="italic text-slate-400">Not reviewed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            {total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button
                  variant="secondary"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail: recorded vs counted, attribution, history and state actions. */}
      {detailId && !detail && !detailError && (
        <Modal title="Reconciliation" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading reconciliation…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Reconciliation" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detailId && detail &&
        (() => {
          const row = detail.reconciliation;
          return (
            <Modal
              title={`Reconciliation — ${row.branch.code} · ${row.businessDate}`}
              onClose={() => setDetailId(null)}
              footer={
                <>
                  {row.status === 'DRAFT' && (
                    <>
                      <Button variant="secondary" onClick={() => openEdit(row)}>
                        Edit draft
                      </Button>
                      <Button disabled={actionBusy} onClick={() => setConfirmSubmit(true)}>
                        Submit for review
                      </Button>
                    </>
                  )}
                  {row.status === 'SUBMITTED' && (
                    <Button disabled={actionBusy} onClick={() => setReviewOpen(true)}>
                      Mark reviewed
                    </Button>
                  )}
                  {row.status === 'REVIEWED' && (
                    <span className="text-sm text-slate-500">
                      Reviewed — record frozen (values are never changed after review)
                    </span>
                  )}
                </>
              }
            >
              <div className="space-y-4">
                {actionError && <ErrorBanner message={actionError} />}

                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <StatusBadge status={row.status} />
                  <Badge>{row.branch.code}</Badge>
                  <span>{row.businessDate} (Asia/Manila)</span>
                  <span className="text-slate-400">
                    {row.recordedAsOf
                      ? `recorded side frozen as of ${formatWhen(row.recordedAsOf)}`
                      : 'draft — compared against live records'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      Recorded
                    </p>
                    <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                      {centsToPesos(row.recorded.totalCents)}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {row.recorded.payments} payment{row.recorded.payments === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      Counted / verified
                    </p>
                    <p className="mt-1 text-lg font-bold tabular-nums text-emerald-700">
                      {row.actualTotalCents === null ? (
                        <span className="text-base text-amber-700">
                          {4 - row.missingMethods.length} of 4 counted
                        </span>
                      ) : (
                        centsToPesos(row.actualTotalCents)
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">preparer&apos;s attestation</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      Difference
                    </p>
                    <p className="mt-1 text-lg font-bold">
                      <DifferenceCell cents={row.differenceCents} />
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">recorded − counted</p>
                  </div>
                </div>

                {row.recordedAfterSubmissionCents > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <span className="font-semibold">Recorded after submission:{' '}</span>
                    {centsToPesos(row.recordedAfterSubmissionCents)} was recorded for this day after
                    the figures were frozen — shown here for transparency and never folded into the
                    attested comparison.
                  </div>
                )}

                {row.unmatchedRecordedCents > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <span className="font-semibold">Recorded without a payment method:{' '}</span>
                    {centsToPesos(row.unmatchedRecordedCents)} cannot be matched to any counted
                    method and is included in the recorded total (disclosed, never dropped).
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-slate-700">By payment method</h3>
                  <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        <tr>
                          <th className="px-3 py-2">Method</th>
                          <th className="px-3 py-2 text-right">Recorded</th>
                          <th className="px-3 py-2 text-right">Counted / verified</th>
                          <th className="px-3 py-2 text-right">Difference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {row.methods.map((line) => (
                          <tr key={line.method} title={line.label}>
                            <td className="px-3 py-2 text-slate-700">
                              {(line.method as PaymentMethod) === 'OTHER'
                                ? `${line.method} (as entered)`
                                : line.method}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                              {centsToPesos(line.recordedCents)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                              {line.actualCents === null ? NOT_COUNTED : centsToPesos(line.actualCents)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <DifferenceCell cents={line.differenceCents} />
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-slate-50/70">
                          <td className="px-3 py-2 font-semibold text-slate-700">Total</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-700">
                            {centsToPesos(row.recorded.totalCents)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-700">
                            {row.actualTotalCents === null ? (
                              <span className="text-amber-700">{4 - row.missingMethods.length} of 4</span>
                            ) : (
                              centsToPesos(row.actualTotalCents)
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <DifferenceCell cents={row.differenceCents} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Method rows sum to the totals above (each payment counted once, per method);
                    cash is a physical count, GCash/Maya are preparer-verified, Other is never
                    classified as cash.
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <h3 className="text-sm font-semibold text-slate-700">Attribution</h3>
                  <p className="text-slate-600">
                    Prepared by <span className="font-medium text-slate-800">{row.preparedBy?.name ?? 'Not recorded'}</span>
                    {row.submittedAt ? (
                      <>
                        {' '}· submitted {formatWhen(row.submittedAt)} by{' '}
                        <span className="font-medium text-slate-800">{row.submittedBy?.name ?? 'Not recorded'}</span>
                      </>
                    ) : (
                      ' · not submitted'
                    )}
                    {row.reviewedAt ? (
                      <>
                        {' '}· reviewed {formatWhen(row.reviewedAt)} by{' '}
                        <span className="font-medium text-slate-800">{row.reviewedBy?.name ?? 'Not recorded'}</span>
                      </>
                    ) : (
                      ' · not reviewed'
                    )}
                  </p>
                  <p className="text-slate-500">
                    <span className="font-medium text-slate-700">Notes:</span>{' '}
                    {row.notes || <span className="italic text-slate-400">None</span>}
                  </p>
                  <p className="text-slate-500">
                    <span className="font-medium text-slate-700">Evidence / reference:</span>{' '}
                    {row.evidenceNote || <span className="italic text-slate-400">None</span>}
                  </p>
                  {row.reviewNotes && (
                    <p className="text-slate-500">
                      <span className="font-medium text-slate-700">Review note:</span> {row.reviewNotes}
                    </p>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <h3 className="text-sm font-semibold text-slate-700">History</h3>
                  {detail.history.length === 0 ? (
                    <p className="mt-1 text-sm text-slate-500">No recorded state changes.</p>
                  ) : (
                    <ol className="mt-2 space-y-2">
                      {detail.history.map((entry, index) => (
                        <li
                          key={`${entry.action}-${index}`}
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <span className="font-medium text-slate-800">{entry.label}</span>
                          <span className="text-slate-500">
                            {' '}· {entry.actorName} ({entry.actorRole}) · {formatWhen(entry.createdAt)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="mt-1 text-xs text-slate-400">
                    Append-only audit trail — submitted and reviewed values are never rewritten.
                  </p>
                </div>
              </div>
            </Modal>
          );
        })()}

      {/* Submit confirmation: freezes the as-of recorded snapshot. */}
      {confirmSubmit && detail && (
        <Modal
          title="Submit reconciliation?"
          onClose={() => {
            if (!actionBusy) setConfirmSubmit(false);
          }}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={actionBusy}
                onClick={() => setConfirmSubmit(false)}
              >
                Cancel
              </Button>
              <Button disabled={actionBusy} onClick={submitDraft}>
                {actionBusy ? 'Submitting…' : 'Confirm submit'}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-slate-600">
            {actionError && <ErrorBanner message={actionError} />}
            <p>
              Submitting freezes the recorded figures <span className="font-semibold">as of now</span>{' '}
              and attributes submission to your account. After submission the record can no longer be
              edited — only reviewed.
            </p>
            <p className="rounded-xl bg-slate-50 p-3 tabular-nums">
              {detail.reconciliation.branch.code} · {detail.reconciliation.businessDate} · difference{' '}
              <DifferenceCell cents={detail.reconciliation.differenceCents} />
              {detail.reconciliation.incomplete && (
                <span className="block text-amber-700">
                  ({4 - detail.reconciliation.missingMethods.length} of 4 methods counted)
                </span>
              )}
            </p>
          </div>
        </Modal>
      )}

      {/* Review dialog: attribution only — a difference is never auto-resolved. */}
      {reviewOpen && detail && (
        <Modal
          title="Mark as reviewed"
          onClose={() => {
            if (!actionBusy) setReviewOpen(false);
          }}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={actionBusy}
                onClick={() => setReviewOpen(false)}
              >
                Cancel
              </Button>
              <Button disabled={actionBusy} onClick={reviewDraft}>
                {actionBusy ? 'Reviewing…' : 'Confirm review'}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-slate-600">
            {actionError && <ErrorBanner message={actionError} />}
            <p>
              Recording your review attributes it to your account with a server timestamp. The
              submitted figures stay exactly as attested — reviewing does not resolve or hide a
              difference.
            </p>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Review note (optional)
              </span>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Anything the next person should know…"
                className="w-full rounded-xl border border-slate-300 bg-panel px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Create / edit-draft form (branch + date immutable after creation). */}
      {form && (
        <Modal
          title={form.mode === 'create' ? 'New reconciliation' : 'Edit draft'}
          onClose={() => {
            if (!formBusy) setForm(null);
          }}
          footer={
            <>
              <Button variant="secondary" disabled={formBusy} onClick={() => setForm(null)}>
                Cancel
              </Button>
              <Button disabled={formBusy} onClick={saveForm}>
                {formBusy
                  ? 'Saving…'
                  : form.mode === 'create'
                    ? 'Create draft'
                    : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {formError && <ErrorBanner message={formError} />}

            {form.mode === 'create' ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Branch</span>
                  <select
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className={inputCls}
                    disabled={branchFilter !== 'ALL'}
                  >
                    {(branchFilter !== 'ALL'
                      ? branches.filter((b) => b.code === branchFilter)
                      : branches
                    ).map((branch) => (
                      <option key={branch.id} value={branch.code}>
                        {branch.code} — {branch.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    Business date (Asia/Manila)
                  </span>
                  <input
                    type="date"
                    value={form.businessDate}
                    max={manilaToday()}
                    onChange={(e) => setForm({ ...form, businessDate: e.target.value })}
                    className={inputCls}
                  />
                </label>
              </div>
            ) : (
              <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {form.branch} · {form.businessDate} — branch and business date are immutable.
              </p>
            )}

            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-700">Actual amounts (₱)</p>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['cash', 'CASH'],
                    ['gcash', 'GCASH'],
                    ['maya', 'MAYA'],
                    ['other', 'OTHER'],
                  ] as const
                ).map(([field, method]) => (
                  <label key={field} className="block">
                    <span className="mb-1 block text-xs font-semibold text-slate-500">
                      {ACTUAL_METHOD_LABELS[method as PaymentMethod]}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={form[field]}
                      onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                      placeholder="Blank = not counted"
                      className={inputCls}
                    />
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Leave a method blank until it is actually counted — blanks are never treated as ₱0.
                Every method is required before submission.
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Notes (required when a difference exists)
              </span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                maxLength={500}
                rows={3}
                placeholder="Explain any difference between recorded and counted…"
                className="w-full rounded-xl border border-slate-300 bg-panel px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Evidence / reference note (optional)
              </span>
              <input
                type="text"
                value={form.evidenceNote}
                onChange={(e) => setForm({ ...form, evidenceNote: e.target.value })}
                maxLength={500}
                placeholder="e.g. GCash reference numbers, deposit slips"
                className={inputCls}
              />
            </label>
          </div>
        </Modal>
      )}
    </section>
  );
}
